import { NoPropertySelected } from "@/components/admin/NoPropertySelected";
import { BrandLoader } from "@/components/brand/BrandLoader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { reportService } from "@/lib/api";
import { formatKrw } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useSelectedProperty } from "@/stores/property";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, MessagesSquare, Percent, Radio, TrendingUp } from "lucide-react";
import { useState } from "react";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toDateString(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function defaultFrom() {
  return toDateString(new Date());
}

function defaultTo() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return toDateString(d);
}

// ─── 안내 공백 ─────────────────────────────────────────────────────────────────
//
// 예약 리포트는 앞으로의 숙박 기간을 보지만 이건 지나간 대화를 보므로 날짜 범위가
// 아니라 "최근 N일" 을 쓴다. 미매칭 = 지식베이스에 대응하는 안내 항목이 없어 자유
// 대화로 답한 질문이고, 답변이 얼마나 좋았는지는 세지 않는다 — 라벨에 그대로 적는다.

const GAP_DAY_OPTIONS = [7, 30, 90] as const;

function GuideGapCard({ propertyId }: { propertyId: string }) {
  const [days, setDays] = useState<number>(30);

  const { data: gap, isLoading } = useQuery({
    queryKey: ["report-guide-gaps", propertyId, days],
    queryFn: () => reportService.guideGaps(propertyId, days),
    enabled: Boolean(propertyId),
  });

  const maxGuideCount = gap && gap.by_guide.length > 0 ? gap.by_guide[0].count : 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessagesSquare className="h-4 w-4" />
          안내 공백 (미매칭 질문)
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          게스트가 보낸 질문 중 등록된 안내 항목에 매칭되지 않아 자유 대화로 답한 질문이에요.
          답변 품질이 아니라 안내 항목이 있었는지만 세요.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 기간 */}
        <div className="flex flex-wrap items-center gap-2">
          {GAP_DAY_OPTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-sm transition-colors",
                d === days
                  ? "border-primary bg-primary/10 font-medium text-primary"
                  : "border-input text-muted-foreground hover:bg-muted"
              )}
            >
              최근 {d}일
            </button>
          ))}
        </div>

        {isLoading ? (
          <BrandLoader className="py-8" size={36} label="불러오는 중…" />
        ) : !gap || gap.question_total === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            선택 기간에 집계된 질문이 없어요. 게스트 대화가 쌓이면 여기에 모여요.
          </p>
        ) : (
          <>
            {/* 요약 */}
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                { label: "질문 수", value: gap.question_total.toLocaleString() },
                { label: "미매칭 질문", value: gap.unmatched_total.toLocaleString() },
                { label: "미매칭 비율", value: `${(gap.unmatched_rate * 100).toFixed(1)}%` },
              ].map(({ label, value }) => (
                <div key={label} className="rounded-lg border border-border/60 px-3 py-2">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="font-display text-xl font-medium tabular-nums">{value}</p>
                </div>
              ))}
            </div>

            {/* 매칭된 안내 분포 */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">매칭된 안내</h3>
              {gap.by_guide.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  매칭된 질문이 없어요. 안내 항목부터 등록해 주세요.
                </p>
              ) : (
                gap.by_guide.map((g) => {
                  const pct = maxGuideCount > 0 ? (g.count / maxGuideCount) * 100 : 0;
                  return (
                    <div key={g.guide_id} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium">{g.title}</span>
                        <span className="tabular-nums text-muted-foreground">{g.count}건</span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* 미매칭 상위 질문 */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">자주 나온 미매칭 질문</h3>
              <p className="text-xs text-muted-foreground">
                게스트가 직접 쓴 문장이에요. 안내에 없어 답하지 못한 것들이라, 여기 있는 질문부터
                안내로 만들면 공백이 줄어요.
              </p>
              {gap.top_unmatched.length === 0 ? (
                <p className="text-sm text-muted-foreground">미매칭 질문이 없어요.</p>
              ) : (
                <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
                  {gap.top_unmatched.map((q) => (
                    <li
                      key={q.question}
                      className="flex items-start justify-between gap-3 px-3 py-2 text-sm"
                    >
                      <span className="break-words">{q.question}</span>
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {q.count}회
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const propertyId = useSelectedProperty();

  const { data: summary, isLoading } = useQuery({
    queryKey: ["report-summary", propertyId, from, to],
    queryFn: () => reportService.summary(propertyId ?? "", from, to),
    enabled: Boolean(propertyId) && Boolean(from) && Boolean(to),
  });

  const channelEntries = summary
    ? Object.entries(summary.revenue_by_channel).sort((a, b) => b[1] - a[1])
    : [];
  const maxRevenue = channelEntries.length > 0 ? channelEntries[0][1] : 0;

  const stats = [
    {
      label: "점유율",
      value: summary ? `${(summary.occupancy_rate * 100).toFixed(1)}%` : "—",
      loading: isLoading,
      icon: Percent,
      tint: "text-primary bg-primary/10",
    },
    {
      label: "총 매출",
      value: summary ? formatKrw(summary.revenue_total) : "—",
      loading: isLoading,
      icon: TrendingUp,
      tint: "text-accent bg-accent/10",
    },
    {
      label: "예약 채널 수",
      value: summary ? String(Object.keys(summary.revenue_by_channel).length) : "—",
      loading: isLoading,
      icon: Radio,
      tint: "text-brass bg-brass/10",
    },
  ];

  if (!propertyId) {
    return <NoPropertySelected />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl font-medium tracking-tight">리포트</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          기간별 점유율과 채널 매출, 그리고 챗봇 안내가 비어 있는 지점을 확인합니다.
        </p>
      </div>

      {/* Date range */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor="report-from" className="text-sm font-medium text-muted-foreground">
            시작
          </label>
          <input
            id="report-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="report-to" className="text-sm font-medium text-muted-foreground">
            종료
          </label>
          <input
            id="report-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        {stats.map(({ label, value, loading, icon: Icon, tint }) => (
          <Card key={label} className="h-full">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <p className="text-sm font-medium text-muted-foreground">{label}</p>
              <span className={cn("flex h-9 w-9 items-center justify-center rounded-lg", tint)}>
                <Icon className="h-4 w-4" />
              </span>
            </CardHeader>
            <CardContent>
              {loading ? (
                <span className="skeleton h-9 w-24" />
              ) : (
                <p className="font-display text-3xl font-medium tabular-nums tracking-tight">
                  {value}
                </p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Channel revenue breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" />
            채널별 매출
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <BrandLoader className="py-8" size={36} label="불러오는 중…" />
          ) : channelEntries.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              선택 기간에 채널 매출 데이터가 없습니다.
            </p>
          ) : (
            <div className="space-y-3">
              {channelEntries.map(([channel, amount]) => {
                const pct = maxRevenue > 0 ? (amount / maxRevenue) * 100 : 0;
                return (
                  <div key={channel} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{channel}</span>
                      <span className="tabular-nums text-muted-foreground">
                        {formatKrw(amount)}
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <GuideGapCard propertyId={propertyId} />
    </div>
  );
}
